<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-user fa-fw fa-lg"></i>
          {{ $label("customer.participants", "Participants") }}
          <router-link
            :to="{ name: 'NewCustomerParticipant' }"
            v-tooltip="{
              content: $label('customer.new_participant', 'New participant'),
              classes: 'custom-tooltip'
            }"
            class="btn btn-v-lightgreen-white pull-right"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>
    <input type="hidden" id="customer-display-all" value="false" />

    <navigation-tabs :customer-id="selectedCustomerId"></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <alert-box
        v-show="failedRequest"
        class="mt-8"
        type="warning"
        :closeable="false"
        :show-icon="false"
      >
        {{
          $label(
            "customer.participants_failed_request",
            "Request failed while loading participants."
          )
        }}
      </alert-box>
      <Table
        v-show="!failedRequest"
        id="participants-table"
        :loading="loading"
        :columns="[
          {
            title: 'Name',
            id: row => `participant-name-${row.id}`,
            value: row => beautifyName(row.first_name, row.last_name),
            onClick: row =>
              $router.push({
                name: 'CustomerParticipantDetails',
                params: {
                  customer_id: selectedCustomerId,
                  participant_id: row.id
                }
              }),
            tooltip: () =>
              '<div class=&quot;tooltip-title-background&quot;><p class=&quot;tooltip-title&quot;><i class=&quot;text-primary fa fa-lg fa-fw fa-info-circle&quot;></i><b>' +
              $label('common.groups', 'Groups') +
              '</b></p></div>' +
              '<p class=&quot;tooltip-content&quot;>' +
              $label(
                'customer.no_assignment_existing',
                'No assignment existing'
              ) +
              '</p>'
          },
          {
            title: 'Email',
            field: 'email',
            id: row => `participant-email-${row.id}`
          },
          {
            title: 'Gender',
            id: row => `participant-gender-${row.id}`,
            value: row =>
              row.gender === 0 ? 'Gender' : row.gender === 1 ? 'Female' : 'Male'
          },
          {
            title: 'Birth date',
            field: 'birthdate',
            id: row => `participant-birthdate-${row.id}`
          }
        ]"
        :actions="[
          {
            type: 'detail',
            onClick: row => {
              $router.push({
                name: 'CustomerParticipantDetails',
                params: {
                  customer_id: selectedCustomerId,
                  participant_id: row.id
                }
              });
            }
          },
          {
            type: 'delete',
            id: row => `delete-participant-button-${row.id}`,
            onClick: row => deleteCustomerParticipant(row.id)
          }
        ]"
        selectable
        :data="participants"
        :meta="metaData"
        @fetchQuery="
          params => {
            fetchParams = params;
            getCustomerParticipants({
              //
            });
          }
        "
      >
        <div id="action-dropdown" class="btn-group">
          <button
            type="button"
            class="btn btn-primary dropdown-toggle"
            data-toggle="dropdown"
          >
            <i class="fa fa-refresh"></i> &nbsp;
            {{ $label("common.actions", "Actions") }} &nbsp;<span
              class="caret"
            ></span>
          </button>
          <ul class="dropdown-menu" role="menu">
            <li>
              <router-link :to="{ name: 'NewCustomerParticipant' }"
                ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
                {{
                  $label(
                    "customer.new_participant_long",
                    "Create new participant"
                  )
                }}</router-link
              >
            </li>
          </ul>
        </div>
      </Table>

      <div class="row mb-8">
        <div class="col-xs-12">&nbsp;</div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api";
import Table from "@/components/Common/Table";
import NavigationTabs from "@/components/Customers/Customer/NavigationTabs";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import Error404 from "@/components/Common/Error404";

import swal from "sweetalert";

export default {
  name: "CustomerParticipants",

  components: {
    Table,
    AlertBox,
    NavigationTabs,
    Error404
  },

  data() {
    return {
      componentKey: 0,
      participants: [],
      loading: false,
      updating: false,
      noDataExist: false,
      participantsLength: null,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      },
      selectedCustomerId: null
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedCustomerId = vm.$route.params.customer_id;
      if (
        vm.$route.query.participant_deleted === true ||
        vm.$route.query.participant_deleted === "true"
      ) {
        swal("Success!", "The participant was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getCustomerParticipants(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.participant_deleted === true) {
      swal("Success!", "The participant was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getCustomerParticipants(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getCustomerParticipants(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.customers = [];
      const endpoint = this.$partnerEndpoint(
        "customers/" + this.selectedCustomerId + "/participants"
      );

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search
          }
        })
        .then(response => {
          this.participants = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteCustomerParticipant(participantId) {
      if (
        this.loading ||
        this.updating ||
        this.selectedCustomerId == null ||
        participantId == null
      ) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The participant will be deleted.",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "participants/" + participantId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "CustomerParticipants",
                  query: { participant_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The participant was deleted.", "success");
                    this.getCustomerParticipants(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete participants.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    goToFirstPage() {
      if (!this.disabledFirstPage) {
        this.currentPage = 1;
        this.getCustomerParticipants(() => {
          //
        });
      }
    },

    goToPreviousPage() {
      if (!this.disabledPreviousPage) {
        this.currentPage--;
        this.getCustomerParticipants(() => {
          //
        });
      }
    },

    goToNextPage() {
      if (!this.disabledNextPage) {
        this.currentPage++;
        this.getCustomerParticipants(() => {
          //
        });
      }
    },

    goToLastPage() {
      if (!this.disabledLastPage) {
        this.currentPage = this.lastPage;
        this.getCustomerParticipants(() => {
          //
        });
      }
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  computed: {
    disabledFirstPage() {
      return this.currentPage === 1;
    },

    disabledPreviousPage() {
      return this.currentPage === 1;
    },

    disabledNextPage() {
      return this.currentPage === this.lastPage;
    },

    disabledLastPage() {
      return this.currentPage === this.lastPage;
    }
  },

  watch: {
    searchFilter() {
      this.getCustomerParticipants(() => {
        //
      });
    }
  }
};
</script>

<style scoped>
.mt-8 {
  margin-top: 1.3rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}
</style>
